// app/javascript/controllers/ferias/form_controller.js
// Controller Stimulus para o formulário nova_ferias / editar férias.
// Identificador: ferias--form

import { Controller } from "@hotwired/stimulus"

import { initFeriasInitialState }   from "../../modules/ferias/form_initial_state"
import { initFeriasFuncionario }    from "../../modules/ferias/form_funcionario"
import { initFeriasPeriodos }       from "../../modules/ferias/form_periodos"
import { initFeriasFracionamento }  from "../../modules/ferias/form_fracionamento"
import { initFeriasAbono }          from "../../modules/ferias/form_abono"
import { initFeriasAdiantamento13 } from "../../modules/ferias/form_adiantamento13"

export default class extends Controller {
  static values = {
    apiFeriasUrl:          String,
    retornaFuncionarioUrl: String,
  }

  connect() {
    const form = this.element.querySelector('#formulario-de-ferias')
    if (!form) return

    // Previne inicialização dupla (ex: turbo:load)
    if (form.dataset.feriasFormInitialized) return
    form.dataset.feriasFormInitialized = '1'

    this._form    = form
    this._modules = []

    // 1) Estado inicial
    this._modules.push(initFeriasInitialState(form))

    // 2) Funcionário: autocomplete callback, form body show/hide, painel de férias, URL param
    this._modules.push(initFeriasFuncionario(form, {
      apiFeriasUrl:          this.apiFeriasUrlValue,
      retornaFuncionarioUrl: this.retornaFuncionarioUrlValue,
    }))

    // 3) Período, fracionamento, abono, adiantamento
    const periodos      = initFeriasPeriodos(form)
    const fracionamento = initFeriasFracionamento(form)
    const abono         = initFeriasAbono(form)
    const adiantamento  = initFeriasAdiantamento13(form)

    this._modules.push(periodos, fracionamento, abono, adiantamento)

    // 4) Aplica estado coerente na primeira renderização
    fracionamento.apply()
    abono.apply()
    adiantamento.apply()
    periodos.apply()

    // 5) Validação client-side antes de submeter
    this._handleSubmit = (e) => this._validateAndContinue(e)
    form.addEventListener('submit', this._handleSubmit, { capture: true })
  }

  disconnect() {
    ;(this._modules || []).forEach(m => {
      try { if (m?.destroy) m.destroy() } catch (_) {}
    })
    this._modules = []

    if (this._form) {
      this._form.removeEventListener('submit', this._handleSubmit, { capture: true })
      delete this._form.dataset.feriasFormInitialized
    }
  }

  // ── Client-side validation ────────────────────────────────────────────────

  _validateAndContinue(e) {
    const form = this._form
    const $j   = window.$j
    if (!$j) return  // sem jQuery, deixa o servidor validar

    const ok = this._runValidations(form, $j)
    if (!ok) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }

  _runValidations(form, $j) {
    let ok = true

    // ── Funcionário ───────────────────────────────────────────────────────
    const funcId  = form.querySelector('#movimentacoes_pessoal_rm_funcionario_id')?.value || ''
    const acInput = form.querySelector('#autocomplete_ferias_rm_funcionario_id')
    const errFunc = form.querySelector('#erro-funcionario')

    if (!funcId) {
      acInput?.classList.add('border-red-400')
      acInput?.classList.remove('border-gray-300')
      if (errFunc) { errFunc.textContent = 'Selecione um funcionário para continuar.'; errFunc.classList.remove('hidden') }
      ok = false
    } else {
      acInput?.classList.remove('border-red-400')
      acInput?.classList.add('border-gray-300')
      if (errFunc) { errFunc.textContent = ''; errFunc.classList.add('hidden') }
    }

    // ── Períodos ──────────────────────────────────────────────────────────
    const getDays = (iniSel, fimSel) => {
      const { calcDias } = window.__feriasPeriodosHelpers || {}
      if (!calcDias) {
        // fallback simples
        const iniV = form.querySelector(iniSel)?.value || ''
        const fimV = form.querySelector(fimSel)?.value || ''
        if (!iniV || !fimV) return null
        const brRe = /^(\d{2})\/(\d{2})\/(\d{4})$/
        const m1   = iniV.match(brRe), m2 = fimV.match(brRe)
        if (!m1 || !m2) return null
        const d1 = new Date(+m1[3], +m1[2] - 1, +m1[1])
        const d2 = new Date(+m2[3], +m2[2] - 1, +m2[1])
        if (d2 < d1) return -1
        return Math.floor((d2 - d1) / 86400000) + 1
      }
      return calcDias(form.querySelector(iniSel)?.value, form.querySelector(fimSel)?.value)
    }

    const setErr = (num, msg) => {
      const el = form.querySelector(`#periodo_${num}_erro`)
      const iniSel = num === 1 ? '#params_inicio'          : `#params_inicio_ferias_${num}`
      const fimSel = num === 1 ? '#params_termino'         : `#params_termino_ferias_${num}`
      if (msg) {
        if (el) { el.textContent = msg; el.classList.remove('hidden') }
        form.querySelector(iniSel)?.classList.add('border-red-400', 'bg-red-50')
        form.querySelector(fimSel)?.classList.add('border-red-400', 'bg-red-50')
      } else {
        if (el) { el.textContent = ''; el.classList.add('hidden') }
        form.querySelector(iniSel)?.classList.remove('border-red-400', 'bg-red-50')
        form.querySelector(fimSel)?.classList.remove('border-red-400', 'bg-red-50')
      }
    }

    const abonoCb  = form.querySelector('#params_abono_pecuniario')
    const diasAbono = abonoCb?.checked
      ? Math.min(Math.max(parseInt(form.querySelector('#params_dias_a_serem_vendidos')?.value || '0', 10), 0), 10)
      : 0
    const disponiveis = 30 - diasAbono

    const p2El = form.querySelector('[data-ferias-periodo="2"]')
    const p3El = form.querySelector('[data-ferias-periodo="3"]')
    const p2Vis = p2El && !p2El.classList.contains('hidden')
    const p3Vis = p3El && !p3El.classList.contains('hidden')

    const p1 = getDays('#params_inicio', '#params_termino')
    if (p1 === null || p1 < 0) {
      setErr(1, p1 === null ? 'Preencha as datas do 1º período de férias.' : 'A data de término não pode ser anterior à de início.')
      ok = false
    } else if (p1 < 5) {
      setErr(1, 'Cada período fracionado deve ter no mínimo 5 dias corridos.')
      ok = false
    } else {
      setErr(1, null)
    }

    if (p2Vis) {
      const p2 = getDays('#params_inicio_ferias_2', '#params_termino_ferias_2')
      if (p2 === null || p2 < 0) {
        setErr(2, p2 === null ? 'Preencha as datas do 2º período.' : 'A data de término não pode ser anterior à de início.')
        ok = false
      } else if (p2 < 5) {
        setErr(2, 'Cada período fracionado deve ter no mínimo 5 dias corridos.')
        ok = false
      } else {
        const restante = disponiveis - (p1 > 0 ? p1 : 0) - p2
        if (restante > 0 && restante < 5) {
          setErr(2, `Este período deixa apenas ${restante} ${restante === 1 ? 'dia' : 'dias'} para o 3º período (mínimo 5 dias).`)
          ok = false
        } else {
          setErr(2, null)
        }
      }
    }

    if (p3Vis) {
      const p3 = getDays('#params_inicio_ferias_3', '#params_termino_ferias_3')
      if (p3 === null || p3 < 0) {
        setErr(3, p3 === null ? 'Preencha as datas do 3º período.' : 'A data de término não pode ser anterior à de início.')
        ok = false
      } else if (p3 < 5) {
        setErr(3, 'Cada período fracionado deve ter no mínimo 5 dias corridos.')
        ok = false
      } else {
        setErr(3, null)
      }
    }

    // ── Valida total > 30 dias ───────────────────────────────────────────
    const totalInput  = form.querySelector('#movimentacoes_pessoal_total_periodo_ferias')
    const totalVal    = parseInt(totalInput?.value || '0', 10)
    const erroTotalEl = form.querySelector('#erro-total-dias')
    if (totalVal > 30) {
      if (erroTotalEl) {
        erroTotalEl.textContent = `O total de dias (${totalVal}) ultrapassa os 30 dias permitidos. Ajuste os períodos.`
        erroTotalEl.classList.remove('hidden')
      }
      if (totalInput) { totalInput.style.backgroundColor = '#FFCCCC'; totalInput.classList.add('border-red-400') }
      ok = false
    } else {
      if (erroTotalEl) { erroTotalEl.textContent = ''; erroTotalEl.classList.add('hidden') }
      if (totalInput) { totalInput.style.backgroundColor = ''; totalInput.classList.remove('border-red-400') }
    }

    // ── Valida regra dos 14 dias ─────────────────────────────────────────
    const p1d = (p1 && p1 > 0) ? p1 : 0
    const p2d = p2Vis ? Math.max(getDays('#params_inicio_ferias_2', '#params_termino_ferias_2') || 0, 0) : 0
    const p3d = p3Vis ? Math.max(getDays('#params_inicio_ferias_3', '#params_termino_ferias_3') || 0, 0) : 0
    const temFrac = (p2Vis && p2d > 0) || (p3Vis && p3d > 0)
    const err14 = form.querySelector('#erro-14-dias')
    if (temFrac && p1d > 0) {
      const maxDias = Math.max(p1d, p2d, p3d)
      if (maxDias < 14) {
        if (err14) { err14.textContent = 'Pelo menos um período de férias deve ter no mínimo 14 dias corridos.'; err14.classList.remove('hidden') }
        ok = false
      } else {
        if (err14) { err14.textContent = ''; err14.classList.add('hidden') }
      }
    } else {
      if (err14) { err14.textContent = ''; err14.classList.add('hidden') }
    }

    // ── Scroll para primeiro erro ────────────────────────────────────────
    if (!ok) {
      const firstErr = form.querySelector(
        '#periodo_1_erro:not(.hidden), #periodo_2_erro:not(.hidden), #periodo_3_erro:not(.hidden),' +
        '#erro-abono:not(.hidden), #erro-14-dias:not(.hidden), #erro-total-dias:not(.hidden),' +
        '#erro-funcionario:not(.hidden)'
      )
      if (firstErr) {
        firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    return ok
  }
}
